import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { fetchWithAuth } from '../../utils/api';
import './QuoteConversion.css';

function QuoteConversion({ quotation, onClose, onConversionComplete }) {
  const { t } = useTranslation('sales');
  const { goToProductionOrders } = useAppNavigate();
  const [conversionOptions, setConversionOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [converting, setConverting] = useState(false);
  const [formData, setFormData] = useState({
    auto_allocate: true,
    line_ids: [] // Empty means convert all lines
  });
  useEffect(() => {
    fetchConversionOptions();
  }, [quotation.quotation_id, quotation.id]);

  const fetchConversionOptions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Handle both 'id' and 'quotation_id' property names
      const quoteId = quotation.quotation_id || quotation.id;
      
      if (!quoteId) {
        throw new Error(t('quoteConversion.errors.noQuotationId'));
      }

      const response = await fetchWithAuth(`/api/sales-orders/conversion-options/${quoteId}`);
      
      if (response.ok) {
        const data = await response.json();
        setConversionOptions(data);
      } else {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (parseError) {
          // If response isn't JSON, use the status text
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error fetching conversion options:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleConvert = async () => {
    try {
      setConverting(true);
      setError(null);

      const quoteId = quotation.quotation_id || quotation.id;
      const conversionRequest = {
        quotation_id: quoteId,
        auto_allocate: formData.auto_allocate,
        line_ids: formData.line_ids.length > 0 ? formData.line_ids : null
      };

      const response = await fetchWithAuth('/api/sales-orders/convert-from-quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(conversionRequest)
      });

      if (response.ok) {
        const result = await response.json();

        // Handle different conversion outcomes
        if (result.status === 'PRODUCTION_ORDERS_CREATED') {
          // Production orders were created - show success message with details
          alert(`✅ ${t('quoteConversion.alerts.replenishmentCreated')}\n\n${result.message}\n\n${t('enhancedQuoteConversion.toasts.productionOrdersCreated')}:\n${result.production_orders_created.map(po => `• ${po.po_number}: ${po.quantity} x ${po.product_code}`).join('\n')}\n\n${t('quoteConversion.alerts.completeProductionFirst')}`);

          // Navigate to production orders to show the created orders
          goToProductionOrders();
        } else {
          // Normal successful conversion
          onConversionComplete(result);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || t('quoteConversion.errors.convertFailed'));
      }
    } catch (err) {
      console.error('Error converting quote:', err);
      setError(err.message);
    } finally {
      setConverting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="quote-conversion-content">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('quoteConversion.loading')}</p>
        </div>
      </div>
    );
  }

  if (error && !conversionOptions) {
    return (
      <div className="quote-conversion-content">
        <div className="error-message">
          <p>{error}</p>
          <button className="btn-primary" onClick={fetchConversionOptions}>
            {t('common:ui.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!conversionOptions) {
    return (
      <div className="quote-conversion-content">
        <div className="error-message">
          <p>{t('quoteConversion.noOptions')}</p>
          <button className="btn-primary" onClick={fetchConversionOptions}>
            {t('quoteConversion.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quote-conversion-content">
      <div className="conversion-container">

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <div className="conversion-body">
          {/* Quote Information */}
          <div className="section">
            <div className="section-header">{t('convertQuoteToProduction.quoteInformation')}</div>
            <div className="section-content">
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('convertQuoteToProduction.quoteNumber')}:</label>
                  <span><strong>{conversionOptions.quotation_number}</strong></span>
                </div>
                <div className="info-item">
                  <label>{t('common:ui.customer')}:</label>
                  <span>{conversionOptions.customer_name}</span>
                </div>
                <div className="info-item">
                  <label>{t('enhancedQuoteConversion.totalLines')}:</label>
                  <span>{conversionOptions.total_lines}</span>
                </div>
                <div className="info-item">
                  <label>{t('quoteConversion.totalValue')}:</label>
                  <span><strong>{formatCurrency(conversionOptions.total_value)}</strong></span>
                </div>
                <div className="info-item">
                  <label>{t('common:ui.status')}:</label>
                  <span className={`status-badge ${conversionOptions.status.toLowerCase()}`}>
                    {conversionOptions.status}
                  </span>
                </div>
                <div className="info-item">
                  <label>{t('quoteConversion.canConvert')}:</label>
                  <span className={conversionOptions.can_convert ? 'text-success' : 'text-danger'}>
                    {conversionOptions.can_convert ? t('common:ui.yes') : t('common:ui.no')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Availability Summary */}
          <div className="section">
            <div className="section-header">{t('quoteConversion.inventoryAvailability')}</div>
            <div className="section-content">
              <div className="availability-summary">
                <div className={`availability-indicator ${conversionOptions.availability_summary.all_available ? 'success' : 'warning'}`}>
                  <div className="indicator-icon">
                    <i className={`fas ${conversionOptions.availability_summary.all_available ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                  </div>
                  <div className="indicator-text">
                    <div className="indicator-label">
                      {conversionOptions.availability_summary.all_available ? t('quoteConversion.allItemsAvailable') : t('quoteConversion.partialAvailability')}
                    </div>
                    <div className="indicator-details">
                      {t('quoteConversion.availableValue')}: {formatCurrency(conversionOptions.availability_summary.available_value)} | 
                      {t('quoteConversion.missingValue')}: {formatCurrency(conversionOptions.availability_summary.missing_value)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fulfillment Options */}
              {conversionOptions.fulfillment_options && conversionOptions.fulfillment_options.length > 0 && (
                <div className="fulfillment-options">
                  <h4>{t('quoteConversion.fulfillmentOptions')}:</h4>
                  <ul>
                    {conversionOptions.fulfillment_options.map((option, index) => (
                      <li key={index} className={`option-${option.type.toLowerCase()}`}>
                        <strong>{option.type}:</strong> {option.description}
                        {option.available_value && (
                          <span> ({t('quoteConversion.available')}: {formatCurrency(option.available_value)})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Line Items */}
          <div className="section">
            <div className="section-header">{t('quoteConversion.quoteLines')}</div>
            <div className="section-content">
              <table className="conversion-lines-table">
                <thead>
                  <tr>
                    <th>{t('quoteConversion.columns.lineNumber')}</th>
                    <th>{t('common:ui.product')}</th>
                    <th>{t('common:ui.quantity')}</th>
                    <th>{t('common:ui.unitPrice')}</th>
                    <th>{t('common:ui.total')}</th>
                    <th>{t('enhancedQuoteConversion.columns.stockAvailable')}</th>
                    <th>{t('quoteConversion.columns.canFulfill')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionOptions.lines.map(line => (
                    <tr key={line.line_id}>
                      <td>{line.line_number}</td>
                      <td>
                        <div>
                          <strong>{line.product_code}</strong>
                        </div>
                        <div className="text-muted">{line.product_name}</div>
                      </td>
                      <td>{line.quantity}</td>
                      <td>{formatCurrency(line.unit_price)}</td>
                      <td><strong>{formatCurrency(line.line_total)}</strong></td>
                      <td>{line.available_stock}</td>
                      <td>
                        <span className={`availability-badge ${line.can_fulfill_from_stock ? 'available' : 'unavailable'}`}>
                          {line.can_fulfill_from_stock ? t('common:ui.yes') : line.needs_production ? t('quoteConversion.productionRequired') : t('common:ui.no')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Stock Requirements & Conversion Options */}
          <div className="section">
            <div className="section-header">{t('quoteConversion.stockRequirementCheck')}</div>
            <div className="section-content">

              {/* Show stock shortage if insufficient inventory */}
              {!conversionOptions.availability_summary.all_available && (
                <div className="stock-shortage-alert" style={{
                  backgroundColor: 'var(--warning-bg)',
                  border: '1px solid var(--warning)',
                  borderRadius: '4px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <i className="fas fa-exclamation-triangle" style={{ color: 'var(--warning)', marginRight: '0.5rem' }}></i>
                    <strong>{t('quoteConversion.insufficientStockTitle')}</strong>
                  </div>
                  <p>{t('quoteConversion.missingValue')}: {formatCurrency(conversionOptions.availability_summary.missing_value)}</p>
                  <p style={{ margin: '0.5rem 0' }}>
                    {t('quoteConversion.insufficientStockMessage')}
                  </p>
                  <button
                    className="btn-warning"
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => {
                      // Navigate to production order creation for stock replenishment
                      // Store data in sessionStorage for production order creation
                      sessionStorage.setItem('stockReplenishmentData', JSON.stringify({
                        purpose: 'stock_replenishment',
                        missing_value: conversionOptions.availability_summary.missing_value,
                        quote_id: quotation.quotation_id || quotation.id
                      }));
                      goToProductionOrders();
                    }}
                  >
                    <i className="fas fa-industry"></i> {t('quoteConversion.createReplenishmentOrders')}
                  </button>
                </div>
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.auto_allocate}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        auto_allocate: e.target.checked
                      }))}
                    />
                    {t('quoteConversion.autoAllocate')}
                  </label>
                  <small>{t('quoteConversion.autoAllocateHelp')}</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="conversion-footer">
          <button 
            className="btn-secondary" 
            onClick={onClose}
            disabled={converting}
          >
            {t('common:ui.cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleConvert}
            disabled={converting || !conversionOptions.can_convert || !conversionOptions.availability_summary.all_available}
          >
            {converting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> {t('convertQuoteToProduction.converting')}
              </>
            ) : conversionOptions.availability_summary.all_available ? (
              <>
                <i className="fas fa-check-circle"></i> {t('quoteConversion.convertStockAvailable')}
              </>
            ) : (
              <>
                <i className="fas fa-exclamation-triangle"></i> {t('quoteConversion.cannotConvert')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuoteConversion;